import { math } from "../../math";
import { ProcessedTextItem,proccessTextItems } from "./processed-text-item";
import { RenderingContext2D } from "./rendering-context-2d";

interface State{
	g:SVGGElement;
	fill:string;
	font:string;
	opacity:number;
	stroke:string;
	strokeDasharray:string;
	strokeDashOffset:number;
	strokeLinecap:CanvasLineCap;
	strokeLinejoin:CanvasLineJoin;
	strokeWidth:number;
	textAlign:CanvasTextAlign;
	textBaseline:CanvasTextBaseline;
}

const defaultState:Omit<State,'g'>={
	fill: 'black',
	font: 'tahoma',
	opacity: 1,
	stroke: 'black',
	strokeDasharray: '',
	strokeDashOffset: 0,
	strokeLinecap: 'square',
	strokeLinejoin: 'miter',
	strokeWidth: 1,
	textAlign: 'left',
	textBaseline: 'alphabetic',
};

function setAttributes(
	e:SVGElement,
	attributes:Record<string,string|number|undefined>
){
	for(const [k,v] of Object.entries(attributes)){
		if(v!==undefined)
			e.setAttribute(k,v.toString());
	}
}

function setStrokeAttributes(
	e:SVGElement,
	state:State,
){
	e.setAttribute('stroke',state.stroke);
	if(state.strokeDasharray!==defaultState.strokeDasharray)
		e.setAttribute('stroke-dasharray',state.strokeDasharray);
	if(state.strokeLinecap!==defaultState.strokeLinecap)
		e.setAttribute('stroke-linecap',state.strokeLinecap);
	if(state.strokeLinejoin!==defaultState.strokeLinejoin)
		e.setAttribute('stroke-linejoin',state.strokeLinejoin);
	if(state.strokeWidth!==defaultState.strokeWidth)
		e.setAttribute('stroke-width',state.strokeWidth.toString());
}

function setTextAttributes(
	e:SVGElement,
	state:State,
){
	if(state.textAlign==='center')
		e.setAttribute('text-anchor','middle');
	if(state.textAlign==='end' || state.textAlign==='right')
		e.setAttribute('text-anchor','end');
}

// const horzAlignToTextAnchor:Record<RenderingContext2D.TextStyle['horzAlign'],string>={
// 	left: 'start',
// 	center: 'middle',
// 	right: 'end',
// 	start: 'start',
// 	end: 'end',
// };

// const vertAlignToBaseline:Record<RenderingContext2D.TextStyle['vertAlign'],string>={
// 	// baseline: 'alphabetic',
// 	top: 'hanging',
// 	middle: 'middle',
// 	bottom: 'text-after-edge',
// };

function setTransformMatrix(
	e:SVGElement,
	m:DOMMatrix,
){
	e.setAttribute('transform',`matrix(${m.a} ${m.b} ${m.c} ${m.d} ${m.e} ${m.f})`);
}

let _scratchCanvas:HTMLCanvasElement;

function getScratchContext(){
	if(!_scratchCanvas){
		_scratchCanvas=document.createElement('canvas');
		_scratchCanvas.width=16;
		_scratchCanvas.height=16;
	}
	const ctx=_scratchCanvas.getContext('2d');
	if(!ctx)
		throw new Error('could not make scratch ctx');
	return ctx;
}

//suppose to be a drop in replacement for CanvasRenderingContext2D
export class SvgRenderingContext2D extends RenderingContext2D{
	constructor(
		private readonly svg:SVGSVGElement,
	){
		super();
		this.state={
			...defaultState,
			g: document.createElementNS('http://www.w3.org/2000/svg','g'),
		};
		svg.append(this.state.g);
	}

	public stateStack:State[]=[];
	private state:State;
	private _pxWidth?:number;
	private pathPrecision=2;

	private path:string[]=[];
	private paths:(SVGGeometryElement)[]=[];

	private beginGroup(){
		if(this.state.g.childElementCount>0){
			const g=document.createElementNS('http://www.w3.org/2000/svg','g');
			this.state.g.appendChild(g);
			this.state={
				...this.state,
				g,
			};
		}
	}

	private appendTransform(transformation:string){
		const base=this.state.g.getAttribute('transform');
		if(base)
			transformation=base+' '+transformation;
		this.state.g.setAttribute('transform',transformation);
		this.clearPxValues();
	}

	public insertPath(){
		const {path}=this;
		if(path.length===2 && path[0][0]==='M' && path[1][0]==='L'){
			const e=document.createElementNS('http://www.w3.org/2000/svg','line');
			const a=path[0].substring(1).split(' ');
			const b=path[1].substring(1).split(' ');
			setAttributes(e,{
				x1: a[0],
				y1: a[1],
				x2: b[0],
				y2: b[1],
			});
			this.paths.push(e);
			return;
		}
		if(path.length>0){
			const e=document.createElementNS('http://www.w3.org/2000/svg','path');
			e.setAttribute('d',path.join(''));
			this.paths.push(e);
		}
	}

	public finishPath(){
		this.insertPath();
		this.path=[];
	}

	public get width(){
		return this.svg.clientWidth;
	}

	public get height(){
		return this.svg.clientWidth;
	}

	public save(){
		this.stateStack.push(this.state);
		const g=document.createElementNS('http://www.w3.org/2000/svg','g');
		this.state.g.appendChild(g);
		this.state={
			...this.state,
			g,
		};
	}

	public restore(){
		this.state=this.stateStack.pop()!;
	}

	public getTransform():DOMMatrix{
		const elements=this.stateStack.map(({g})=>g);
		elements.push(this.state.g);
		const matrices=elements.map(e=>e.transform.baseVal.consolidate()?.matrix).filter(v=>!!v).reverse();
		const m=new DOMMatrix();
		for(const _m of matrices){
			m.multiplySelf(_m);
		}
		return m;
	}

	public setTransform(m:DOMMatrix){
		this.beginGroup();
		setTransformMatrix(this.state.g,m);
		this.clearPxValues();
	}

	public resetTransform(){
		const m=this.getTransform();
		this.setTransform(m.invertSelf());
	}

	public transform(a:number, b:number, c:number, d:number, e:number, f:number):void{
		this.beginGroup();
		this.appendTransform(`matrix(${a} ${b} ${c} ${d} ${e} ${f})`);
	}

	public translate(x:number, y:number){
		this.beginGroup();
		this.appendTransform(`translate(${x} ${y})`);
	}

	public scale(x:number, y:number){
		this.beginGroup();
		this.appendTransform(`scale(${x} ${y})`);
	}

	public rotate(angle:number){
		this.beginGroup();
		this.appendTransform(`rotate(${angle*math.R2D})`);
	}

	private clearPxValues(){
		this._pxWidth=undefined;
		this.pathPrecision=2;
	}

	private generatePxValues(){
		if(this._pxWidth===undefined){
			let m=this.getTransform();
			m=m.inverse();
			this._pxWidth=Math.sqrt(m.c*m.c+m.d*m.d)*this.pxUnitFactor;
			let precision=math.log10(this._pxWidth/100);
			precision=math.max(0,-(math.floor(precision)-1));
			this.pathPrecision=precision;
		}
	}

	public pxWidth(v=1){
		this.generatePxValues();
		return v*this._pxWidth!;
	}

	public pxUp(){
		return new math.Vec2(0,1);
	}

	public get globalAlpha(){
		return this.state.opacity;
	}
	public set globalAlpha(v){
		this.beginGroup();
		this.state.g.setAttribute('opacity',v.toString());
		this.state.opacity=v;
	}

	public get fillStyle(){
		return this.state.fill;
	}
	public set fillStyle(v){
		this.state.fill=v;
	}

	public setLineDash(segments:number[]){
		this.state.strokeDasharray=segments.join(' ');
	}

	public get lineDashOffset(){
		return this.state.strokeDashOffset;
	}
	public set lineDashOffset(v){
		this.state.strokeDashOffset=v;
	}

	public get lineWidth(){
		return this.state.strokeWidth;
	}
	public set lineWidth(v){
		this.state.strokeWidth=v;
	}

	public get lineCap(){
		return this.state.strokeLinecap;
	}
	public set lineCap(v){
		this.state.strokeLinecap=v;
	}

	public get lineJoin(){
		return this.state.strokeLinejoin;
	}
	public set lineJoin(v){
		this.state.strokeLinejoin=v;
	}

	public get strokeStyle(){
		return this.state.stroke;
	}
	public set strokeStyle(v){
		this.state.stroke=v;
	}

	public beginPath(){
		this.finishPath();
		this.path=[];
		this.paths=[];
	}

	public moveTo(x:number, y:number){
		this.generatePxValues();
		this.path.push(`M${x.toFixed(this.pathPrecision)} ${y.toFixed(this.pathPrecision)}`);
	}

	public lineTo(x:number, y:number){
		this.generatePxValues();
		this.path.push(`L${x.toFixed(this.pathPrecision)} ${y.toFixed(this.pathPrecision)}`);
	}

	public closePath(){
		this.path.push(`Z`);
	}

	public override pathTo(points:Iterable<math.Vec2Like>){
		this.generatePxValues();
		const it=points[Symbol.iterator]();
		let r=it.next();
		if(r.done)
			return;
		this.path.push(`M${r.value.x.toFixed(this.pathPrecision)} ${r.value.y.toFixed(this.pathPrecision)}`);
		r=it.next();
		while(!r.done){
			this.path.push(`L${r.value.x.toFixed(this.pathPrecision)} ${r.value.y.toFixed(this.pathPrecision)}`);
			r=it.next();
		}
	}

	public rect(x:number, y:number, width:number, height:number){
		this.finishPath();
		const e=document.createElementNS('http://www.w3.org/2000/svg','rect');
		setAttributes(e,{x,y,width,height});
		this.paths.push(e);
	}

	public circle(cx:number, cy:number, r:number){
		this.finishPath();
		const e=document.createElementNS('http://www.w3.org/2000/svg','circle');
		setAttributes(e,{cx,cy,r});
		this.paths.push(e);
	}

	public ellipse(cx:number, cy:number, rx:number, ry:number, rotation:number, startAngle:number, endAngle:number, counterclockwise?:boolean){
		this.finishPath();
		if(startAngle===0 && endAngle>=Math.PI*2){
			if(rx===ry){
				const e=document.createElementNS('http://www.w3.org/2000/svg','circle');
				setAttributes(e,{cx,cy,r:rx});
				this.paths.push(e);
			}else{
				const e=document.createElementNS('http://www.w3.org/2000/svg','ellipse');
				setAttributes(e,{cx,cy,rx,ry});
				this.paths.push(e);
			}
		}else{
			//NEEDS TO BE ADDED
		}
	}

	public arc(x:number, y:number, radius:number, startAngle:number, endAngle:number, counterclockwise?:boolean){
		this.finishPath();
		//TO BE ADDED
	}

	public stroke(path?:Path2D){
		this.insertPath();
		for(const e of this.paths){
			setStrokeAttributes(e,this.state);
			e.setAttribute('fill','none');
		}
		this.state.g.append(...this.paths);
	}

	public fill():void;
	public fill(path:Path2D):void;
	public fill(path?:Path2D){
		this.insertPath();
		for(const e of this.paths){
			e.setAttribute('fill',this.state.fill);
			if(e.hasAttribute('stroke'))
				e.setAttribute('paint-order','stroke');
		}
		this.state.g.append(...this.paths);
	}

	public clip():void;
	public clip(fillRule:CanvasFillRule):void;
	public clip(fillRule?:CanvasFillRule){
		//NEEDS TO BE ADDED
	}

	public get font(){
		return this.state.font;
	}
	public set font(v){
		this.state.font=v;
	}

	public get textAlign(){
		return this.state.textAlign;
	}
	public set textAlign(v){
		this.state.textAlign=v;
	}

	public get textBaseline(){
		return this.state.textBaseline;
	}
	public set textBaseline(v){
		this.state.textBaseline=v;
	}

	public measureText(text:string):TextMetrics{
		throw new Error('measureText no implimented on svg');
		//return null;//this.ctx.measureText(text);
	}

	public strokeText(text:string, x:number, y:number, maxWidth?:number){
		const e=document.createElementNS('http://www.w3.org/2000/svg','text');
		e.textContent=text;
		setAttributes(e,{
			x,
			y,
			'alignment-baseline': this.state.textBaseline,
		});
		setStrokeAttributes(e,this.state);
		setTextAttributes(e,this.state);
		e.style.font=this.state.font;
		this.state.g.appendChild(e);
	}

	public fillText(text:string, x:number, y:number, maxWidth?:number){
		if(this.state.g.lastElementChild instanceof SVGTextElement){
			const e=this.state.g.lastElementChild;
			if(
				e.textContent===text
				&& e.getAttribute('x')===x.toString()
				&& e.getAttribute('y')===y.toString()
				&& !e.hasAttribute('fill')
			){
				e.setAttribute('fill',this.state.fill);
				e.setAttribute('paint-order','stroke');
				return;
			}
		}
		const e=document.createElementNS('http://www.w3.org/2000/svg','text');
		e.textContent=text;
		setAttributes(e,{
			x,
			y,
			fill: this.state.fill,
			'alignment-baseline': this.state.textBaseline,
		});
		setTextAttributes(e,this.state);
		e.style.font=this.state.font;
		this.state.g.appendChild(e);
	}

	public clearRect(x:number, y:number, w:number, h:number){
	}

	public fillRect(x:number, y:number, width:number, height:number){
		const e=document.createElementNS('http://www.w3.org/2000/svg','rect');
		setAttributes(e,{
			x,
			y,
			width,
			height,
			fill: this.state.fill
		});
		this.state.g.appendChild(e);
	}

	public strokeRect(x:number, y:number, width:number, height:number){
		const e=document.createElementNS('http://www.w3.org/2000/svg','rect');
		setAttributes(e,{
			x,
			y,
			width,
			height,
		});
		setStrokeAttributes(e,this.state);
		this.state.g.appendChild(e);
	}

    public drawImage(image:CanvasImageSource, dx:number, dy:number):void;
    public drawImage(image:CanvasImageSource, dx:number, dy:number, dw:number, dh:number):void;
    public drawImage(image:CanvasImageSource, sx:number, sy:number, sw:number, sh:number, dx:number, dy:number, dw:number, dh:number):void;
	public drawImage(image:CanvasImageSource, sx:number, sy:number, sw?:number, sh?:number, dx?:number, dy?:number, dw?:number, dh?:number){
		//TO BE ADDED
	}

	public processText(
		items:RenderingContext2D.TextItem[],
		style:RenderingContext2D.TextStyle={},
	){
		const pxWidth=this.pxWidth();
		const ctx=getScratchContext();
		ctx.resetTransform();
		return proccessTextItems(
			ctx,
			pxWidth,
			this.pxUnitFactor,
			this.defaultTextStyle,
			this.textTransformer,
			items,
			style,
		);
	}

	public drawProcessedText(
		items:ProcessedTextItem[],
		callback?:(transform:DOMMatrix, item:ProcessedTextItem, index:number)=>void,
	):void{
		if(!items.length)
			return;

		const coordToPixel=this.getTransform();
		const style=items[0].style;

		const g=document.createElementNS('http://www.w3.org/2000/svg','g');
		setTransformMatrix(g,coordToPixel.inverse());
		setAttributes(g,{
			'stroke-linecap': 'round',
			'dominant-baseline': "hanging",
			'stroke': style.outline?.color.toString(),
			'stroke-width': style.outline?style.outline.width*2:undefined,
			'stroke-linejoin': 'round',
			'paint-order': 'stroke',
		});
		g.style.font=items[0].font;

		for(const item of items){
			const [pos,off,rot]=item.pixelOrientation(coordToPixel,this.viewDownVecPx);
			const transform=`translate(${pos.x.toFixed(1)} ${pos.y.toFixed(1)}) rotate(${(rot*math.R2D).toFixed(1)}) translate(${off.x.toFixed(1)} ${off.y.toFixed(1)})`;

			const {style,outerBox,lines}=item;
			const {background}=style;
			if(background){
				const e=document.createElementNS('http://www.w3.org/2000/svg','rect');
				const attributes:Record<string,string|number>={
					x: outerBox.min.x,
					y: outerBox.min.y,
					width: outerBox.size('x'),
					height: outerBox.size('y'),
					fill: background.color.toString(),
					stroke: 'none',
					transform,
				};
				if(background.outline){
					attributes['stroke']=background.outline.color;
					attributes['stroke-width']=background.outline.width*2;
					attributes['stroke-linejoin']='miter';
				}else
					setAttributes(e,attributes);
				g.appendChild(e);
			}

			for(const {x,y,line} of lines){
				const e=document.createElementNS('http://www.w3.org/2000/svg','text');
				e.textContent=line;
				const attributes:Record<string,string|number>={
					x: x.toFixed(1),
					y: y.toFixed(1),
					fill: style.color?.toString() ?? 'white',
					transform,
				};
				setAttributes(e,attributes);
				g.appendChild(e);
			}
		}
		this.state.g.appendChild(g);
	}
}
